/*
================================ HOLD FOR BUYER (admin) =========================================

An admin freeze that changes what the BUYER sees and nothing else. Toggled from the order-detail
header; the API writes the `holdForBuyer` flag on the order and pushes one entry onto `order_status`
(see controllers/order.js holdForBuyer in medimny-api).

WHAT ADMIN SEES
Everything, unchanged. `requested` is never touched by a hold, so an order held for the buyer stays
the New / Requested / Accepted / OnHold order it was in every list, filter and report. The only
adjustments are cosmetic and live in `adminLogStatus` / `adminLastLogStatus` below, and both exist
to keep one signal honest: while an order is frozen, admin reads "New and OnHold", and when it is
not, admin reads the plain status the order actually carries.

  the RELEASE entry     "New and UnHold" reads as a plain 'New'. It is a bookkeeping string, not a
                        status anybody works an order by, and once the hold is off the order is
                        simply the New one it always was.

  a still-held 'New'    accepting a change request pushes a plain 'New' entry on top of the hold
                        (medimny-api controllers/order.js, status 'accept', which also sets
                        `requested` to 'Accepted'). The order has not moved - it is still frozen -
                        so admin keeps reading it as "New and OnHold", exactly as before the buyer
                        accepted. Otherwise the one at-a-glance cue that the order is held would
                        vanish from every list the moment a change request went through.

The hold entry itself is deliberately left showing as itself - that IS the admin's cue that the
order is frozen, alongside the button in the header flipping to "UnHold for buyer".

Keep the strings below in step with medimny-api (controllers/order.js) and with
util/holdForBuyer.js in medimny-web, which turns the same entries into the buyer's and the seller's
views (`sellerLastLogStatus` there mirrors `adminLastLogStatus` here).
*/
export const HOLD_FOR_BUYER_STATUS = 'New and OnHold';
export const UNHOLD_FOR_BUYER_STATUS = 'New and UnHold';

/*
THE RUN-ON FORMS

The two entries were originally stored without the spaces, and every hold written before the rename
still carries the run-on spelling. scripts/renameHoldForBuyerLogStatus.js in medimny-api rewrites
them, but nothing here may assume it has run: an archived order, a stale replica or an un-migrated
environment can still hand us the old value. So every comparison goes through `isHoldStatus` /
`isUnholdStatus`, and anything that renders a raw log entry - a badge, the Order Logs dialog, the
tracking timeline - goes through `logStatusLabel`, which upgrades an old value on the way to the
screen.

Once the migration has run everywhere and no order carries the run-on form, these three can go.
*/
export const LEGACY_HOLD_FOR_BUYER_STATUS = 'NewandOnHold';
export const LEGACY_UNHOLD_FOR_BUYER_STATUS = 'NewandUnHold';

export const isHoldStatus = (status) =>
  status === HOLD_FOR_BUYER_STATUS || status === LEGACY_HOLD_FOR_BUYER_STATUS;

export const isUnholdStatus = (status) =>
  status === UNHOLD_FOR_BUYER_STATUS || status === LEGACY_UNHOLD_FOR_BUYER_STATUS;

// One log status as it should be READ - the run-on forms upgraded, everything else passed through.
export const logStatusLabel = (status) => (isHoldStatus(status)
  ? HOLD_FOR_BUYER_STATUS
  : isUnholdStatus(status) ? UNHOLD_FOR_BUYER_STATUS : status);

// The `requested` values the API will accept a toggle on. Anything else is an order that has
// already been processed, shipped, delivered or cancelled, and the button is hidden for it - the
// API refuses those too, so this only saves the admin a pointless round trip.
export const HOLD_FOR_BUYER_ALLOWED_REQUESTED = ['New', 'Accepted', 'Requested', 'OnHold'];

export const canHoldForBuyer = (orderDetails) =>
  HOLD_FOR_BUYER_ALLOWED_REQUESTED.indexOf(orderDetails && orderDetails.requested) > -1;

// One log status as admin should read it, with no order to consult - the release entry rewritten,
// everything else passed through. Callers holding the whole order want `adminLastLogStatus`, which
// can also apply the still-held 'New' rule; see the note above.
export const adminLogStatus = (status) => (isUnholdStatus(status) ? 'New' : status);

// The last entry on an order's log as ADMIN should read it. Tolerant of an order that somehow
// arrives with no log at all, which the callers' plain array indexing was not.
export const adminLastLogStatus = (orderData) => {
  const logs = (orderData && orderData.order_status) || [];
  const status = logs.length > 0 ? logs[logs.length - 1].status : '';
  // Still frozen, and the log's last word is a plain 'New' (a change request accepted on top of the
  // hold): the order is held, so that is what admin reads.
  if (!!(orderData && orderData.holdForBuyer) && status === 'New') return HOLD_FOR_BUYER_STATUS;
  return logStatusLabel(adminLogStatus(status));
};
